import { signOut as firebaseSignOut, User } from 'firebase/auth';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { catchError } from '../../errors/utils/handleError';
import { getAuth } from '../../firebase';

export interface AuthInfo {
  isAdmin: boolean;
  refresh: () => Promise<void>;
  uid: string;
  signOut: () => Promise<void>;
}

const LOGGED_OUT_USER: AuthInfo = {
  isAdmin: false,
  refresh: () => Promise.resolve(),
  uid: '',
  signOut: () => Promise.resolve(),
};

export const AuthContext = createContext<AuthInfo>(LOGGED_OUT_USER);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const firebaseAuth = getAuth();

  const [uid, setUid] = useState<string>(
    firebaseAuth.currentUser?.uid ?? LOGGED_OUT_USER.uid
  );
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const signOut = useCallback(async () => {
    return firebaseSignOut(firebaseAuth).finally(() => {
      setIsAdmin(false);
      setUid(LOGGED_OUT_USER.uid);
    });
  }, [firebaseAuth]);

  useEffect(() => {
    firebaseAuth
      .authStateReady()
      .then(() => {
        setLoading(false);
      })
      .catch(catchError);
  }, [firebaseAuth]);

  const refresh = useCallback(async () => {
    const currentUser = getAuth().currentUser;

    if (!currentUser) {
      return Promise.resolve();
    }

    void currentUser.getIdTokenResult(true).then((tokenResult) => {
      setIsAdmin(!!tokenResult.claims.superAdmin);
      setUid(currentUser.uid);
    });
  }, []);

  const handleAuthStateChanged = useCallback(
    (currentUser: User | null) => {
      if (!currentUser?.uid || (uid && isAdmin)) {
        return;
      }

      void currentUser.getIdTokenResult().then((tokenResult) => {
        setIsAdmin(!!tokenResult.claims.superAdmin);
        setUid(currentUser.uid);
      });
    },
    [isAdmin, uid]
  );

  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged(handleAuthStateChanged);

    return () => {
      unsubscribe();
    };
  }, [firebaseAuth, handleAuthStateChanged]);

  if (loading) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ isAdmin, refresh, uid, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
